import { FactiiiRequirement, type FactiiiTag } from '../domains/factiiis';

export const checkTagRequirementsMet = (
  tag: Omit<
    FactiiiTag,
    | 'isNewFactiii'
    | 'isRemovedFactiii'
    | 'isPinnedFactiii'
    | 'avatarKey'
    | 'spaceSlug'
    | 'isBot'
    | 'isRetired'
  >,
  // newPhoto: CameraCapturedPicture | undefined,
  post: {
    uploads:
      | { uri: string; isVideo?: boolean }[]
      | {
          id: string;
        }[];
    content: string;
    postFactiiisCount: { factiiiId: number }[];
  }
) => {
  const result = tag.requirements
    .map((requirement) => {
      const status = getRequirementStatus(
        requirement as FactiiiRequirement,
        post,
        tag
      );
      return status;
    })
    .filter((x) => x !== null);

  // if (tag.requirements.includes(FactiiiRequirement.CAMERA) && !newPhoto) {
  //   errors.push("Please take a picture to use ${tag.name} factiii.");
  // }

  return result;
};

export const getRequirementStatus = (
  requirement: FactiiiRequirement,
  post: {
    uploads: { uri: string; isVideo?: boolean }[] | { id: string }[];
    content: string;
    postFactiiisCount: { factiiiId: number }[];
  },
  tag: Omit<
    FactiiiTag,
    | 'isNewFactiii'
    | 'isRemovedFactiii'
    | 'isPinnedFactiii'
    | 'avatarKey'
    | 'spaceSlug'
    | 'isBot'
    | 'isRetired'
  >
): {
  completed: boolean;
  description: string;
  requirement: FactiiiRequirement;
} | null => {
  switch (requirement) {
    case FactiiiRequirement.MEDIA_REQUIRED:
      return {
        completed: Boolean(post.uploads) && post.uploads.length > 0,
        description: 'Add a photo or video',
        requirement
      };
    case FactiiiRequirement.DATA_REQUIRED:
      return {
        completed: Boolean(tag.postFactiiis[0].data),
        description: 'Fill in the extra details',
        requirement
      };
    case FactiiiRequirement.POST_REFERENCE_REQUIRED:
      return {
        completed: Boolean(tag.postFactiiis[0].references?.length),
        description: 'Link to another post',
        requirement
      };
    case FactiiiRequirement.SOURCE_REQUIRED:
      if (tag.postFactiiis[0].factiiiReferences?.length === 0) {
        return {
          completed: false,
          description: 'Add a source link',
          requirement
        };
      }
      if (
        tag.postFactiiis[0].factiiiReferences?.some(
          (reference) =>
            !reference.data ||
            !/(http(s)?:\/\/.)?(www\.)?[-a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)/.test(
              reference.data
            )
        )
      ) {
        return {
          completed: false,
          description: 'Add a valid source link',
          requirement
        };
      }
      return {
        completed: true,
        description: 'Source added',
        requirement
      };
    case FactiiiRequirement.CONTENT_REQUIRED:
      return {
        completed: Boolean(post.content),
        description: 'Write something in your post',
        requirement
      };
    case FactiiiRequirement.WIKI:
      return {
        completed: Boolean(post.content) && post.content.length >= 120,
        description: 'Keep it short (max 120 characters)',
        requirement
      };
    case FactiiiRequirement.LOCATION_REQUIRED:
      if (
        !/^[-+]?([1-8]?\d(\.\d*)?|90(\.0*)?)\s*,\s*[-+]?((1[0-7]\d|([1-9]?\d))(\.\d*)?|180(\.0*)?)$/.test(
          `${tag.postFactiiis[0].spaceTimes?.[0]?.latitude},${tag.postFactiiis[0].spaceTimes?.[0]?.longitude}`
        )
      ) {
        return {
          completed: false,
          description: 'Pick a location',
          requirement
        };
      }
      return {
        completed: true,
        description: 'Location added',
        requirement
      };
    case FactiiiRequirement.APPROXIMATE_LOCATION:
      if (
        !/^[-+]?([1-8]?\d(\.\d*)?|90(\.0*)?)\s*,\s*[-+]?((1[0-7]\d|([1-9]?\d))(\.\d*)?|180(\.0*)?)$/.test(
          `${tag.postFactiiis[0].spaceTimes?.[0]?.latitude},${tag.postFactiiis[0].spaceTimes?.[0]?.longitude}`
        )
      ) {
        return {
          completed: false,
          description: 'Pick a nearby location',
          requirement
        };
      }
      return {
        completed: true,
        description: 'Nearby location added',
        requirement
      };
    case FactiiiRequirement.DEVICE_LOCATION:
      if (
        !/^[-+]?([1-8]?\d(\.\d*)?|90(\.0*)?)\s*,\s*[-+]?((1[0-7]\d|([1-9]?\d))(\.\d*)?|180(\.0*)?)$/.test(
          `${tag.postFactiiis[0].spaceTimes?.[0]?.latitude},${tag.postFactiiis[0].spaceTimes?.[0]?.longitude}`
        )
      ) {
        return {
          completed: false,
          description: 'Allow location access',
          requirement
        };
      }
      return {
        completed: true,
        description: 'Location detected',
        requirement
      };
    case FactiiiRequirement.TIME_REQUIRED:
      return {
        completed:
          Boolean(tag.postFactiiis[0].spaceTimes?.[0]?.timestamp) &&
          !tag.postFactiiis[0].spaceTimes[0].dateTimeError,
        description: 'Pick a date and time',
        requirement
      };
    case FactiiiRequirement.INTEGER_REQUIRED:
      return {
        // PostFactiii.number, not data: the value has its own column.
        completed: Number.isFinite(tag.postFactiiis[0].number),
        description: 'Enter a number',
        requirement
      };
    case FactiiiRequirement.HUMAN_SOURCE:
    case FactiiiRequirement.GOVERNMENT_SOURCE:
    case FactiiiRequirement.ENTERPRISE_SOURCE:
    case FactiiiRequirement.ANONYMOUS_SOURCE:
      //check if valid url
      if (
        !/(http(s)?:\/\/.)?(www\.)?[-a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)/.test(
          tag.postFactiiis[0]?.data || ''
        )
      ) {
        return {
          completed: false,
          description: 'Add a link to your source',
          requirement
        };
      }
      return {
        completed: true,
        description: 'Source link added',
        requirement
      };
    case FactiiiRequirement.NO_POST_DUPLICATES:
      return {
        completed: !post.postFactiiisCount?.some(
          (postFactiii) => postFactiii.factiiiId === tag.factiiiId
        ),
        description: 'Already used on this post',
        requirement
      };
    case FactiiiRequirement.CAMERA:
      return {
        completed:
          (Boolean(tag.postFactiiis[0].uploads) &&
            tag.postFactiiis[0].uploads.length > 0) ||
          Boolean(tag.postFactiiis[0].photo),
        description: 'Take a photo',
        requirement
      };
    case FactiiiRequirement.MEDIA_MATCH: {
      // Exactly one image: the server embeds it (post-level upload, so count
      // post.uploads). Videos aren't supported — only the still-image path exists.
      const uploads = post.uploads ?? [];
      const hasVideo = uploads.some((u) => 'isVideo' in u && u.isVideo);
      return {
        completed: uploads.length === 1 && !hasVideo,
        description: hasVideo
          ? 'Use a single image (videos are not supported)'
          : uploads.length > 1
            ? 'Add exactly one image (only one allowed)'
            : 'Add one image to match',
        requirement
      };
    }
    // return {
    //   completed: true,
    //   description: 'Location coordinates will be randomized by ~0.5 miles',
    //   requirement
    // };
    // case FactiiiRequirement.POSTFACTIII_REQUIRED:
    // // Check if the post has a required factiii present
    // // The required factiii should be available via the relation
    // const requiredFactiiiId = tag.requiredFactiiiId;

    // if (!requiredFactiiiId) {
    //   return {
    //     completed: false,
    //     description: 'No required factiii specified for this factiii',
    //     requirement
    //   };
    // }

    // // Check if the post has the required factiii
    // const hasRequiredFactiii = post.postFactiiisCount?.some(
    //   (factiiiCount) => factiiiCount.factiiiId === requiredFactiiiId
    // );

    // if (!hasRequiredFactiii) {
    //   return {
    //     completed: false,
    //     description: `Post must have the required factiii present`,
    //     requirement
    //   };
    // }

    // return {
    //   completed: true,
    //   description: 'Required factiii is present on post',
    //   requirement
    // };
    default:
      return null;
  }
};
